import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { setMobileMenuOpen } from '../../Actions/Navigation.action';
import Icon from '../Icon';
import SideMenu from '../SideMenu/SideMenu';
import MobileNavigationItem from './ui/MobileNavigationItem';
import { translate } from '../../Services/translation';
import { useTypedSelector } from '../../Hooks/useTypedSelector';
import clsx from 'clsx';
import LanguageSelectTrigger from '../LanguageSelect/LanguageSelectTrigger';
import LanguageSelectModal from '../LanguageSelect/LanguageSelectModal';
import LoginContainer from '../Login/LoginContainer';
import { channelPicker } from '../../Reducers/ChannelPicker.reducer';

const NavigationMobile = () => {
  const dispatch = useDispatch();
  const {
    mobileMenuOpen,
    logoUrl,
    contentLinks,
    instagramUrl,
    facebookUrl,
    pinterestUrl,
    myPagesUrl,
    youtubeUrl,
    extraLinksInMobileMenu,
  } = useTypedSelector<any>((state) => state.navigation);
  const [blockElements, setBlockElements] = useState<any[]>([]);

  const userIsLoggedIn = useTypedSelector(
    (state) => state.userInfo.current != null
  );

  const [loginOpen, setLoginOpen] = useState<boolean>(false);
  const [currentLink, setCurrentLink] = useState<any>();
  const [previousLink, setPreviousLink] = useState<any>();
  const { open: languageSelectVisible } = useTypedSelector(
    (state) => state.channelPicker
  );

  const handleCloseClick = useCallback(() => {
    if (languageSelectVisible) {
      dispatch(channelPicker.setOpen(false));
      return;
    }
    dispatch(setMobileMenuOpen(false));
  }, [dispatch, languageSelectVisible]);

  useEffect(() => {
    const blockElementsContainer =
      document.getElementById('mega-menu-blocks');
    const blockElements = blockElementsContainer
      ? Array.from(blockElementsContainer.children)
      : [];
    setBlockElements(blockElements);
  }, []);

  const blockContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!blockContainerRef?.current) return;
    blockContainerRef.current.innerHTML = '';
    for (let block of blockElements) {
      blockContainerRef.current.appendChild(block);
    }
  }, [blockContainerRef, blockElements]);

  const onBackClick = useCallback(() => {
    setPreviousLink(previousLink?.previousLink);
    setCurrentLink(previousLink);
  }, [previousLink]);

  const onLinkClick = useCallback(
    (link) => {
      link.previousLink = previousLink;
      setPreviousLink(currentLink);
      setCurrentLink(link);
    },
    [currentLink, previousLink]
  );

  const links = useMemo(
    () =>
      (currentLink?.links ?? contentLinks).filter((l) => !l.rightAligned),
    [contentLinks, currentLink?.links]
  );

  const Header = useCallback(
    () => (
      <div className="flex flex-1 items-center justify-between">
        {!currentLink && (
          <>
            <a href="/">
              <img className="max-h-8" src={logoUrl} alt="Logo" />
            </a>
            <div
              className={clsx('-m-[1rem] cursor-pointer p-4')}
              onClick={() => handleCloseClick()}
            >
              <Icon
                className="h-[1.5rem] w-[1.5rem] text-gray-900"
                name="close"
              />
            </div>
          </>
        )}
        {currentLink && (
          <>
            {!languageSelectVisible ? (
              <div
                className="flex cursor-pointer items-center"
                onClick={onBackClick}
              >
                <Icon
                  className="-ml-2 h-[1.5rem] w-[1.5rem] text-gray-900"
                  name="chevron-left"
                />
                <span className="h6 mt-[3px] text-gray-500">
                  {translate('general.back')}
                </span>
              </div>
            ) : (
              <img className="max-h-8" src={logoUrl} alt="Logo" />
            )}
            <div
              className="-m-[1rem] cursor-pointer p-4"
              onClick={() => handleCloseClick()}
            >
              <Icon
                className="h-[1.5rem] w-[1.5rem] text-gray-900"
                name="close"
              />
            </div>
          </>
        )}
      </div>
    ),
    [
      currentLink,
      handleCloseClick,
      languageSelectVisible,
      logoUrl,
      onBackClick,
    ]
  );

  const NavigationLoginContainer = useCallback(() => {
    return loginOpen ? (
      <div className="absolute bottom-0 left-0 right-0 top-0 bg-white px-4 py-3">
        <div
          className="mb-4 flex cursor-pointer items-center"
          onClick={() => setLoginOpen(false)}
        >
          <Icon
            className="-ml-2 h-[1.5rem] w-[1.5rem] text-gray-500"
            name="chevron-left"
          />
          <span className="h6 mt-[3px] text-gray-500">
            {translate('general.back')}
          </span>
        </div>
        <LoginContainer />
      </div>
    ) : null;
  }, [loginOpen]);

  const LanguageSelectWrapper = useCallback(
    ({ children }: { children: any }) => (
      <div
        className={clsx(
          'absolute bottom-0 z-50 w-full',
          languageSelectVisible && 'h-full',
          !languageSelectVisible
            ? 'pointer-events-none'
            : 'pointer-events-auto'
        )}
      >
        <div
          className={clsx(
            'grid h-full bg-white transition-all duration-200',
            languageSelectVisible ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
          )}
        >
          <div className="overflow-hidden">{children}</div>
        </div>
      </div>
    ),
    [languageSelectVisible]
  );

  const Content = useCallback(
    () => (
      <div className="relative flex-1 gap-4 overflow-y-auto border-y border-gray-100 p-0 pt-3.5 ">
        <ul className="flex flex-col">
          {currentLink &&
            currentLink.links.length > 0 &&
            currentLink.name && (
              <MobileNavigationItem
                link={currentLink}
                onLinkClick={onLinkClick}
                isCurrentLink={true}
              />
            )}
          {links.map((link, idx) => (
            <MobileNavigationItem
              link={link}
              key={'mobile' + link.url + link.name + idx}
              onLinkClick={onLinkClick}
            />
          ))}
        </ul>
        {currentLink && currentLink.extraInfo?.value && (
          <div
            className="[&_a]:h4 [&_a]: [&_a]:flex [&_a]:cursor-pointer [&_a]:px-4 [&_a]:py-3 [&_a]:text-gray-700"
            dangerouslySetInnerHTML={{
              __html: currentLink.extraInfo.value,
            }}
          ></div>
        )}
        {currentLink && currentLink.blockSystemIds?.length > 0 && (
          <div
            className={clsx(
              'flex w-full overflow-x-auto p-4 scrollbar-none [&_>div]:flex-shrink-0',
              currentLink.blockSystemIds.length == 1 &&
                '[&_>div]:w-[calc(50%_-_1rem)]',
              currentLink.blockSystemIds.length == 2 &&
                'gap-4 [&_>div]:w-[calc(50%_-_0.5rem)]',
              currentLink.blockSystemIds.length > 2 &&
                'gap-4 [&_>div]:w-[calc(50%_-_1rem)]'
            )}
          >
            {blockElements
              ?.filter(
                (b) =>
                  currentLink.blockSystemIds.indexOf(
                    b.dataset['litiumBlockId']
                  ) != -1
              )
              .map((block, idx) => (
                <div
                  key={idx}
                  dangerouslySetInnerHTML={{ __html: block?.innerHTML }}
                ></div>
              ))}
          </div>
        )}
        {!currentLink && (
          <ul>
            {extraLinksInMobileMenu &&
              extraLinksInMobileMenu.length > 0 &&
              extraLinksInMobileMenu.map((link, idx) => (
                <li key={link.href + idx}>
                  <a
                    className="block px-4 py-3 font-garamond text-h6"
                    href={link.href}
                  >
                    {link.text}
                  </a>
                </li>
              ))}
            <li className="font-garamond text-h6">
              {userIsLoggedIn ? (
                <a
                  className="block px-4 py-3 font-garamond text-h6"
                  href={myPagesUrl}
                >
                  {translate('mypage.page-name')}
                </a>
              ) : (
                <button
                  className="px-4 py-3"
                  onClick={() => setLoginOpen(true)}
                >
                  {translate('general.login')}
                </button>
              )}
            </li>
          </ul>
        )}
        <NavigationLoginContainer />
        <LanguageSelectWrapper>
          <LanguageSelectModal />
        </LanguageSelectWrapper>
      </div>
    ),
    [
      LanguageSelectWrapper,
      NavigationLoginContainer,
      blockElements,
      currentLink,
      extraLinksInMobileMenu,
      links,
      myPagesUrl,
      onLinkClick,
      userIsLoggedIn,
    ]
  );

  const footer = () => (
    <div
      key={'footer'}
      className="flex w-full items-center justify-between"
    >
      <div>
        {instagramUrl && (
          <a
            target="_blank"
            className="p-2.5"
            href={instagramUrl}
            rel="noreferrer"
            aria-label="Instagram"
          >
            <Icon className="h-5 w-5" name="instagram-logo-light" />
          </a>
        )}
        {facebookUrl && (
          <a
            target="_blank"
            className="p-2.5"
            href={facebookUrl}
            rel="noreferrer"
            aria-label="Facebook"
          >
            <Icon className="h-5 w-5" name="facebook-logo-light" />
          </a>
        )}
        {pinterestUrl && (
          <a
            target="_blank"
            className="p-2.5"
            href={pinterestUrl}
            rel="noreferrer"
            aria-label="Pinterest"
          >
            <Icon className="h-5 w-5" name="pinterest-logo-light" />
          </a>
        )}
        {youtubeUrl && (
          <a
            target="_blank"
            className="p-2.5"
            href={youtubeUrl}
            rel="noreferrer"
            aria-label="Youtube"
          >
            <Icon className="h-5 w-5" name="youtube-logo-light" />
          </a>
        )}
      </div>
      <LanguageSelectTrigger
        onClick={() =>
          dispatch(channelPicker.setOpen(!languageSelectVisible))
        }
        languageSelectActive={languageSelectVisible}
      />
    </div>
  );

  useEffect(() => {
    setTimeout(() => {
      setLoginOpen(false);
    }, 200);
  }, [mobileMenuOpen]);

  useEffect(() => {
    if (mobileMenuOpen) {
      setCurrentLink(null);
    }
  }, [mobileMenuOpen]);

  return (
    <>
      <SideMenu
        className="z-[60] max-w-[480px] rounded"
        from="right"
        id="navigation-mobile"
        open={mobileMenuOpen}
        onClosed={() => dispatch(setMobileMenuOpen(false))}
        components={{ Header, Content, Footer: footer }}
        showCloseButton={false}
      ></SideMenu>
    </>
  );
};
export default NavigationMobile;
